import { Empty, Flex, Spin, Tabs, Typography, useMessagedAsync } from "@payping/web-components";
import { moment } from "@payping/utils/dateUtils";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { useMemo, useState } from "react";
import { InstallmentPaymentFilterStatus, InstallmentStatus } from "@payping/api/app/api/bnplConsumerTypes";
import type { InstallmentResponse, InstallmentsListResponse } from "@payping/api/app/api/bnplConsumerTypes";
import { bnplConsumerGetInstallmentList } from "@payping/redux/actions";
import { useSearchParams } from "react-router";
import HomeOverviewInstallmentDetailsPage from "./InstallmentDetail";
import HomeOverviewPageInstallmentCard from "./InstallmentCard";
import { HOME_INSTALLMENT_FILTER } from "./keys";

function getPersianMonthName(date: moment.Moment) {
  return date.locale("fa").format("jMMMM");
}

const HomeOverviewPage = () => {
  const { t } = useAppTranslation();

  const [searchParams, setSearchparams] = useSearchParams();

  const InstallmentCurrentItem: HOME_INSTALLMENT_FILTER = [
    HOME_INSTALLMENT_FILTER.AHEAD,
    HOME_INSTALLMENT_FILTER.DELAYED,
  ].includes(searchParams.get("status") as never)
    ? (searchParams.get("status") as HOME_INSTALLMENT_FILTER)
    : HOME_INSTALLMENT_FILTER.DELAYED;

  const [installmentModal, setInstallmentModal] = useState<{ data?: InstallmentResponse; open: boolean }>({
    open: false,
    data: undefined,
  });
  const dispatch = useAppDispatch();
  const installmentListStatus = useMessagedAsync(
    async () => dispatch(bnplConsumerGetInstallmentList(undefined, InstallmentPaymentFilterStatus.NotPaid)),
    [dispatch],
    { success: false },
  );

  const installmentList = useAppSelector((state) => state.bnplConsumer.installmentList);

  const installmentListOrdered = useMemo(() => {
    const orderList: Record<HOME_INSTALLMENT_FILTER, Record<string, InstallmentsListResponse["installments"]>> = {
      delayed: {},
      ahead: {},
    };
    if (installmentListStatus.loading || installmentListStatus.error) {
      return orderList;
    }
    (installmentList?.installments ?? []).forEach((item) => {
      if (item.isPaid) {
        return;
      }
      const month = getPersianMonthName(moment(item.dueDate ?? new Date()));
      if (
        item.installmentStatus === InstallmentStatus.Delayed ||
        item.installmentStatus === InstallmentStatus.Defaulted ||
        item.installmentStatus === InstallmentStatus.SalaryDeductionWaiting
      ) {
        orderList.delayed = { ...orderList.delayed, [month]: [...(orderList.delayed[month] || []), item] };
      } else {
        orderList.ahead = { ...orderList.ahead, [month]: [...(orderList.ahead[month] || []), item] };
      }
    });

    // for the first time if user
    // if there is no delayed installment, show ahead installment
    if (!Object.keys(orderList.delayed).length && !searchParams.get("status")) {
      setSearchparams((pre) => {
        pre.set("status", HOME_INSTALLMENT_FILTER.AHEAD);
        return pre;
      });
    }
    return orderList;
  }, [
    installmentList?.installments,
    installmentListStatus.error,
    installmentListStatus.loading,
    searchParams,
    setSearchparams,
  ]);

  const onChangeInstallmentTab = (key?: string) => {
    setSearchparams((pre) => {
      pre.set("status", key as HOME_INSTALLMENT_FILTER);
      return pre;
    });
  };

  const handleCloseInstallmentModal = () => {
    setInstallmentModal({ open: false, data: undefined });
  };
  const handleOpenInstallmentModal = (data: InstallmentResponse) => {
    setInstallmentModal({ open: true, data });
  };

  return (
    <>
      <Flex gap={"large"} vertical className="tw:mt-4! tw:w-full tw:p-4!">
        <Typography.Text>{t("common.installmentsCalender")}</Typography.Text>
        {installmentList || !installmentListStatus.loading ? (
          <Flex vertical>
            <Tabs
              className="tw:m-0! tw:hidden tw:max-lg:block"
              activeKey={InstallmentCurrentItem}
              items={[
                { key: HOME_INSTALLMENT_FILTER.DELAYED, label: t("common.installmentStatus.delayed") },
                { key: HOME_INSTALLMENT_FILTER.AHEAD, label: t("common.installmentStatus.aHead") },
              ]}
              onChange={onChangeInstallmentTab}
            />
            <Flex className="tw:w-full" gap="middle" vertical>
              {Object.entries(installmentListOrdered?.[InstallmentCurrentItem])?.map(([key, value]) => (
                <Flex className="tw:w-full" gap="middle" vertical key={key}>
                  <Flex className="tw:w-full" align="center" gap={"small"}>
                    <Typography.Text className="tw:m-0!">{key}</Typography.Text>
                  </Flex>
                  <Flex vertical className="tw:w-full" gap={"small"}>
                    {value.map((item) => (
                      <HomeOverviewPageInstallmentCard
                        handlePay={() => handleOpenInstallmentModal(item)}
                        key={item.installmentCode}
                        amount={item.installmentFinalAmount}
                        dueDate={item.dueDate ? moment(item.dueDate) : undefined}
                        shopName={item.merchantName || ""}
                        penaltyAmount={item.delayPenaltyAmount}
                        installmentNumber={item.number}
                      />
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>
        ) : (
          <Flex className="tw:h-44 tw:w-full" justify="center" align="center">
            <Spin />
          </Flex>
        )}
      </Flex>
      {!installmentListStatus.loading && !Object.entries(installmentListOrdered?.[InstallmentCurrentItem]).length ? (
        <Empty />
      ) : null}
      {installmentModal.open && installmentModal.data ? (
        <HomeOverviewInstallmentDetailsPage
          data={installmentModal.data}
          onClose={handleCloseInstallmentModal}
          open={installmentModal.open}
        />
      ) : null}
    </>
  );
};

export default HomeOverviewPage;
